import {
    CashoutHistoryGraph,
    CashoutHistoryGraphProps
} from "@tipico/jack-time";
import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { CashOutGraphFiltersProps } from "../../components/CashOutGraphFilters/CashOutGraphFilters";
import {
    StatsPanel,
    StatsPanelProps
} from "../../components/StatsPanel/StatsPanel";
import { TicketDetailsStreamProps } from "../../containers/CashoutCointainer/CashoutContainer";
import AnimatedDialog from "../AnimatedDialog/AnimatedDialog";
import {
    CashoutButton,
    CashoutButtonProps
} from "../CashoutButton/CashoutButton";
import {
    CashoutDisableProps,
    CashoutError
} from "../CashoutError/CashoutError";
import { CashOutGraphFilters } from "../CashOutGraphFilters/CashOutGraphFilters";
import { CashOutLabel } from "../CashOutLabel/CashOutLabel";

// tslint:disable-next-line:no-var-requires
const styles = require("./Cashout.scss");

export function Cashout({
    sendBackAction,
    cashoutHandler,
    isBlocked,
    isError,
    cashoutValue,
    currentValue,
    currentTimestamp,
    ...props
}: CashoutButtonProps &
    CashOutGraphFiltersProps &
    CashoutHistoryGraphProps &
    StatsPanelProps &
    TicketDetailsStreamProps &
    CashoutDisableProps) {
    const [showDisclaimer, dialog] = renderDisclaimerDialog();

    if (isError) {
        return <CashoutError sendBackAction={sendBackAction} />;
    }
    return (
        <>
            <CashOutLabel label={props.ticketNumber} />
            <CashOutGraphFilters
                activeFilter={props.activeFilter}
                onFilterClick={props.onFilterClick}
            />
            <div className={classNames(styles.chartAndStatistics)}>
                <CashoutHistoryGraph
                    data={props.data}
                    lastPeriodToShow={props.activeFilter}
                    formatValue={props.formatValue}
                />
                <StatsPanel
                    minValue={props.minValue}
                    currentValue={props.ticketStake}
                    maxValue={props.maxValue}
                    maxPrize={props.ticketMaxPrize}
                    showDisclaimer={showDisclaimer}
                />
            </div>
            <CashoutButton
                isBlocked={isBlocked}
                cashoutValue={currentValue}
                // prettier-ignore
                cashoutHandler={cashoutHandler.bind(undefined, { currentValue, currentTimestamp })}
            >
                {props.children}
            </CashoutButton>
            {dialog}
        </>
    );
}

function renderDisclaimerDialog(): [() => void, JSX.Element] {
    const [disclaimerVisible, setDisclaimerVisible] = useState(false);
    const showDisclaimer = useCallback(() => setDisclaimerVisible(true), []);
    const hideDisclaimer = useCallback(() => setDisclaimerVisible(false), []);
    const { t } = useTranslation("cashout");

    const dialog = (
        <AnimatedDialog
            visible={disclaimerVisible}
            onClose={hideDisclaimer}
            wrapClassName={styles.disclaimerPopup}
            title={t("DisclaimerDialogTitle")}
        >
            <Trans i18nKey="DisclaimerNotes" t={t}>
                <ol>
                    <li>
                        The Cashout History Graph is for information purposes
                        only. Tipico engages third parties for this service and
                        is therefore not liable for transmission errors with
                        regards to betting data and results shown on the Cashout
                        History Graph.
                    </li>
                    <li>
                        Tipico disclaims liability or any incidental or
                        consequential damages and assumes no responsibility or
                        liability for any loss or damage suffered by any person
                        as a result of the use or misuse of any of the
                        information or content contained herein. Tipico further
                        assumes no liability for any loss or damage suffered as
                        a result of the use, misuse, or reliance on the
                        information and content herein.
                    </li>
                    <li>
                        Except in cases of malicious intent or gross negligence
                        on the part of Tipico, there are absolutely no claims
                        for compensation due to transmission errors, delays, or
                        the misuse or manipulation of data during transmission
                        via the internet or for other errors occurring during
                        transmission of data and results.
                    </li>
                </ol>
            </Trans>
        </AnimatedDialog>
    );

    return [showDisclaimer, dialog];
}
